import {
  useCallback,
  useDeferredValue,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { Terminal as TerminalIcon } from 'lucide-react';
import { extractHeadings, type PlatformBridge, type RecentFile, type ToolHost } from '@slate/core';
import { useDocStore, type OpenDoc } from '../store/docStore';
import { useTerminalCommandHistory, topCommands } from '../tools/terminal/commandHistory';
import { openPath } from '../store/docActions';
import { useCommands, buildCtx } from './commandRegistry';
import { usePluginCommands } from './pluginCommands';
import type { PluginHost } from '../plugins/PluginHost';
import { parsePaletteInput } from './paletteInput';
import { parseZoomInput } from './numberParse';
import { rankByScore } from './rank';
import { searchActiveDoc, searchOpenTabs, searchWorkspace } from './searchProviders';
import { createPathProvider, joinPath } from './pathProvider';
import type {
  Command,
  CommandArgs,
  CommandCtx,
  CommandParam,
  CommandRunResult,
  Hit,
  HitKind,
  HitSection,
  PaletteInput,
} from './types';

const PAGE = 5;

export interface CommandPaletteProps {
  platform: PlatformBridge;
  /**
   * Host handed to tool `commands` contributions surfaced in the palette (e.g.
   * "Terminal: New"). When absent, no tool commands are listed — the palette
   * still works with its built-in commands.
   */
  toolHost?: ToolHost;
  /**
   * The app plugin host. When provided, installed (enabled) plugins' declared
   * commands appear in the palette under the "Plugins" group and re-derive live
   * on install/enable/disable. Omitted in tests that don't exercise plugins.
   */
  pluginHost?: PluginHost | null;
}

/**
 * Compute the logical section a hit belongs to. Section is NOT stored on the
 * hit (commit 8f23fc3); it's derived at render time from the hit kind plus the
 * active document id (so doc-name / doc-content hits split into "Active doc" vs
 * "Open tabs").
 */
function sectionOf(hit: HitKind, activeDocId: string | null): HitSection {
  switch (hit.kind) {
    case 'command':
      return 'Commands';
    case 'tab':
      return 'Open tabs';
    case 'recent':
      return 'Recent';
    case 'docName':
      return hit.docId === activeDocId ? 'Active doc — name' : 'Open tabs';
    case 'docContent':
      return hit.docId === activeDocId ? 'Active doc — content' : 'Open tabs content';
    case 'wsName':
      return hit.isDir ? 'Workspace folders' : 'Workspace files';
    case 'wsContent':
      return 'Workspace content';
    case 'pathEntry':
    case 'openCurrentDir':
      return 'Path';
    case 'heading':
      return 'Outline';
    case 'enumValue':
      return 'Enum';
    case 'numberPreset':
      return 'Numeric';
    case 'shellCommand':
      return 'Terminal';
    case 'help':
      return 'Help';
  }
}

/**
 * Coerce the raw positional argument tokens from the parsed input into typed
 * `CommandArgs` keyed by each param's name. Number params go through the zoom
 * parser (so `%`/`x`/bare all work); heading params are line numbers; everything
 * else is passed through as a trimmed string. Empty/undefined tokens are skipped
 * so a command runs with whatever the user actually supplied.
 */
export function buildArgsFromInput(cmd: Command, parsed: PaletteInput): CommandArgs {
  const args: CommandArgs = {};
  cmd.params?.forEach((p, i) => {
    if (p.kind === 'shell') {
      // Free-form: the whole remainder after the slash is one shell command.
      const q = parsed.query.trim();
      if (q) args[p.name] = q;
      return;
    }
    const token = parsed.args[i];
    if (token === undefined || token === '') return;
    if (p.kind === 'number') {
      const r = parseZoomInput(token, p.range ?? [0, Number.POSITIVE_INFINITY]);
      if (r) args[p.name] = r.value;
    } else if (p.kind === 'heading') {
      const n = Number(token);
      if (Number.isFinite(n)) args[p.name] = n;
    } else {
      args[p.name] = token;
    }
  });
  return args;
}

/** Find a command by an exact slash, honoring `aliases`. */
export function findCommandBySlash(commands: Command[], slash: string): Command | undefined {
  return commands.find((c) => c.slash === slash || c.aliases?.includes(slash));
}

/** Build the command run-context from the live store snapshot + platform. */
function buildCtxFromState(platform: PlatformBridge): CommandCtx {
  return buildCtx(platform, useDocStore.getState());
}

/**
 * Compute the path of `abs` relative to `root` for display in recursive search
 * results. Falls back to the full path when `abs` is not under `root` (so a hit
 * still renders something sensible). Leading slashes are trimmed so the relative
 * form has no leading separator (`docs/spec.md`, not `/docs/spec.md`).
 */
function relativeTo(root: string, abs: string): string {
  return abs.startsWith(root) ? abs.slice(root.length).replace(/^[\\/]+/, '') : abs;
}

/** True for POSIX-absolute `/foo` and Windows-absolute `C:\foo` / `C:/foo`. */
function isAbsolutePath(p: string): boolean {
  return p.startsWith('/') || /^[A-Za-z]:[\\/]/.test(p);
}

/**
 * Resolve a path-arg token to an absolute candidate path. Used by the
 * "open this folder" finalizer to decide whether the typed token corresponds to
 * an existing directory. `baseDir` is the home directory when the token started
 * with `~/` (stripped by the caller) or the first workspace root otherwise.
 * Trailing slashes are normalized off — `docs/` and `docs` resolve identically.
 */
function resolveCandidatePath(token: string, baseDir: string): string | null {
  const t = token.replace(/[\\/]+$/, '');
  if (t.length === 0) return null;
  if (isAbsolutePath(t)) return t;
  if (!baseDir || baseDir === '/') return null;
  return joinPath(baseDir, t);
}

export function CommandPalette({ platform, toolHost, pluginHost }: CommandPaletteProps): ReactElement | null {
  const open = useDocStore((s) => s.paletteOpen);
  const setOpen = useDocStore((s) => s.setPaletteOpen);
  const docs = useDocStore((s) => s.docs);
  const activeId = useDocStore((s) => s.activeId);
  const workspaceRoots = useDocStore((s) => s.workspaceRoots);
  const historyEntries = useTerminalCommandHistory((s) => s.entries);
  // User-tunable palette behavior (Settings → Command Palette). Reading via
  // selectors keeps the constants live: a change in Settings takes effect on
  // the very next render, without remounting the palette.
  const mixedCap = useDocStore((s) => s.paletteMixedCap);
  const mixedOrder = useDocStore((s) => s.paletteMixedOrder);
  const workspaceDebounceMs = useDocStore((s) => s.paletteWorkspaceDebounceMs);
  const minWorkspaceQuery = useDocStore((s) => s.paletteMinWorkspaceQuery);

  const baseCommands = useCommands(platform, toolHost);
  const pluginCmds = usePluginCommands(pluginHost ?? null);
  const commands = useMemo(
    () => (pluginCmds.length > 0 ? [...baseCommands, ...pluginCmds] : baseCommands),
    [baseCommands, pluginCmds],
  );
  const [raw, setRaw] = useState('');
  const [selected, setSelected] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const restoreFocusRef = useRef<Element | null>(null);
  // Hover-focus is "armed" only after the user actually moves the pointer.
  // When the user is typing or arrow-navigating, the list re-renders and a
  // different row slides under a stationary cursor, firing mouseenter on
  // whichever item happens to land there — focusing a random row. We disarm
  // on every list change (see effect below) and re-arm on real pointermove.
  const hoverArmedRef = useRef(false);

  const listboxId = useId();
  const optionPrefix = useId();

  // Defer the parsed input so typing stays responsive even when downstream
  // searches (active-doc line scan, open-tab scans, command filtering) cost a
  // few milliseconds. React keeps the input in sync immediately and re-derives
  // results when idle. Workspace search has its own 200ms debounce on top.
  const deferredRaw = useDeferredValue(raw);
  const parsed: PaletteInput = useMemo(() => parsePaletteInput(deferredRaw), [deferredRaw]);

  const activeDoc = useMemo(() => docs.find((d) => d.id === activeId), [docs, activeId]);

  // ── Open/close lifecycle: reset query, focus input, restore focus on close ──
  useEffect(() => {
    if (open) {
      restoreFocusRef.current = document.activeElement;
      // A `/`-shortcut open pre-seeds command mode; otherwise start empty. Read
      // + consume the seed once so it doesn't leak into the next open.
      const seed = useDocStore.getState().paletteSeed ?? '';
      setRaw(seed);
      useDocStore.getState().consumePaletteSeed();
      setSelected(0);
      const id = requestAnimationFrame(() => inputRef.current?.focus());
      return () => cancelAnimationFrame(id);
    }
    const el = restoreFocusRef.current as HTMLElement | null;
    if (el && typeof el.focus === 'function') el.focus();
    // Drop the path provider so a workspace-root change between openings is
    // picked up next time (its rootDir is baked in at creation).
    pathProviderRef.current = null;
    return undefined;
  }, [open]);

  // ── Command matching (sync) ──
  const commandHits: Hit[] = useMemo(() => {
    if (!open) return [];
    if (parsed.mode === 'command') {
      // While still typing the command name (no trailing space yet), filter the
      // catalogue by `/typedname`. Once a command is chosen, arg completions are
      // produced by the dedicated arg-hits effect below.
      if (!parsed.commandSlash) {
        // In '/' mode, show all matched commands including disabled — this is a
        // catalogue browse. The renderer applies data-disabled styling so users
        // see why a command is currently unavailable.
        const q = '/' + parsed.query;
        // Rank each command by the BEST of its trigger slashes (slash + aliases)
        // so it is findable under any trigger, then dedupe to one row per id.
        const expanded = commands.flatMap((c) =>
          [c.slash, ...(c.aliases ?? [])]
            .filter((s): s is string => !!s)
            .map((s) => ({ c, s })),
        );
        const ranked = rankByScore(expanded, (e) => e.s, q);
        const seen = new Set<string>();
        const out: Hit[] = [];
        for (const e of ranked) {
          if (seen.has(e.c.id)) continue;
          seen.add(e.c.id);
          out.push({ score: 0, hit: { kind: 'command' as const, commandId: e.c.id } });
        }
        return out;
      }
      return [];
    }
    if (parsed.mode === 'mixed' && parsed.query) {
      // In mixed-search mode, filter disabled — users want runnable suggestions,
      // not catalogue rows.
      const matches = rankByScore(
        commands.filter((c) => c.enabled),
        (c) => c.name,
        parsed.query,
      ).slice(0, 5);
      return matches.map((c) => ({ score: 0, hit: { kind: 'command' as const, commandId: c.id } }));
    }
    return [];
  }, [open, parsed, commands]);

  // ── Active doc + open-tabs content (sync, mixed mode) ──
  const syncHits: Hit[] = useMemo(() => {
    if (!open || parsed.mode !== 'mixed' || parsed.query === '') return [];
    return [...searchActiveDoc(activeDoc, parsed.query), ...searchOpenTabs(docs, activeId, parsed.query)];
  }, [open, parsed.mode, parsed.query, activeDoc, docs, activeId]);

  // ── Workspace search (async, debounced 200ms, abortable) ──
  const wsTokenRef = useRef(0);
  const [wsHits, setWsHits] = useState<Hit[] | 'loading' | null>(null);
  useEffect(() => {
    if (!open || parsed.mode !== 'mixed' || parsed.query.length < minWorkspaceQuery) {
      setWsHits(null);
      return undefined;
    }
    const token = ++wsTokenRef.current;
    setWsHits('loading');
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      void searchWorkspace(parsed.query, workspaceRoots, platform, controller.signal).then((hits) => {
        if (wsTokenRef.current === token) setWsHits(hits);
      });
    }, workspaceDebounceMs);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [open, parsed.mode, parsed.query, workspaceRoots, platform, minWorkspaceQuery, workspaceDebounceMs]);

  // ── Empty-state: open tabs, then recent files ──
  const [tabHits, setTabHits] = useState<Hit[]>([]);
  const [recents, setRecents] = useState<RecentFile[]>([]);
  useEffect(() => {
    if (!open || parsed.mode !== 'empty') {
      setTabHits([]);
      return undefined;
    }
    setTabHits(docs.map((d) => ({ score: 0, hit: { kind: 'tab' as const, docId: d.id } })));
    let cancelled = false;
    void platform.recentFiles().then((rs) => {
      if (cancelled) return;
      const next = rs.slice(0, 8);
      // Skip a no-op state update (avoids an extra render when there are none).
      setRecents((prev) => (prev.length === 0 && next.length === 0 ? prev : next));
    });
    return () => {
      cancelled = true;
    };
  }, [open, parsed.mode, docs, platform]);

  const emptyHits: Hit[] = useMemo(() => {
    if (parsed.mode !== 'empty') return [];
    const recentHits: Hit[] = recents.map((r) => ({
      score: 0,
      hit: { kind: 'recent' as const, path: r.ref.path ?? '', name: r.ref.name },
    }));
    return [...tabHits, ...recentHits];
  }, [parsed.mode, tabHits, recents]);

  // ── Outline mode (`@`): headings of the active doc, filtered ──
  const outlineHits: Hit[] = useMemo(() => {
    if (parsed.mode !== 'outline') return [];
    const headings = extractHeadings(activeDoc?.text ?? '');
    const q = parsed.query.toLowerCase();
    const filtered = q ? headings.filter((h) => h.text.toLowerCase().includes(q)) : headings;
    return filtered.map((h) => ({
      score: 0,
      hit: { kind: 'heading' as const, line: h.line, depth: h.depth, text: h.text },
    }));
  }, [parsed.mode, parsed.query, activeDoc]);

  // ── Help mode (`?`): three static rows ──
  const helpHits: Hit[] = useMemo(() => {
    if (parsed.mode !== 'help') return [];
    return [
      { score: 0, hit: { kind: 'help', prefix: '/', description: 'Run a command' } },
      { score: 0, hit: { kind: 'help', prefix: '#', description: 'Jump to a heading' } },
      { score: 0, hit: { kind: 'help', prefix: '?', description: 'Show this help' } },
    ];
  }, [parsed.mode]);

  // ── Argument completions for `/<cmd> <arg>` ──
  const activeParam: CommandParam | undefined = useMemo(() => {
    if (parsed.mode !== 'command' || !parsed.commandSlash) return undefined;
    const cmd = findCommandBySlash(commands, '/' + parsed.commandSlash);
    // The positional slot under the cursor (path/enum/number/…). When there is
    // no positional param at this index, fall back to a free-form shell param so
    // multi-word remainders like `/cmd ls -la` (activeArgIndex 1) still resolve
    // to the shell suggestion branch. `buildArgsFromInput` always passes the
    // whole `parsed.query` for a shell param, so the full remainder runs.
    return cmd?.params?.[parsed.activeArgIndex] ?? cmd?.params?.find((p) => p.kind === 'shell');
  }, [parsed, commands]);

  const [pathHits, setPathHits] = useState<Hit[]>([]);
  const pathProviderRef = useRef<ReturnType<typeof createPathProvider> | null>(null);
  // The rootDir baked into the live path provider (createPathProvider has no
  // public accessor for it). When the effective base directory changes — e.g.
  // a `~/` token expands to the home dir — we recreate the provider so the
  // per-level browse rebases correctly.
  const pathProviderRootRef = useRef<string | null>(null);

  // Reset / lazily create the path provider when leaving or entering a path arg.
  useEffect(() => {
    if (activeParam?.kind !== 'path') {
      pathProviderRef.current = null;
      pathProviderRootRef.current = null;
      setPathHits([]);
    }
  }, [activeParam]);

  useEffect(() => {
    if (activeParam?.kind !== 'path') return undefined;

    const token = parsed.activeToken;
    let cancelled = false;

    void (async () => {
      // Branch 1: `~/` expansion — rebase the listing to the home directory and
      // browse the remainder per-level. Falls through to plain browse when the
      // platform can't resolve a home dir (web/sandboxed).
      let workingToken = token;
      let baseDir = workspaceRoots[0]?.path ?? '/';
      let tildeExpanded = false;
      if (token.startsWith('~/') && typeof platform.homeDir === 'function') {
        const home = await platform.homeDir();
        if (cancelled) return;
        if (home) {
          baseDir = home;
          workingToken = token.slice(2); // strip leading '~/'
          tildeExpanded = true;
        }
      }

      // "Open this folder" finalizer: when the parent command can target a
      // folder (`/open` or `/open-folder`) and the typed token resolves to an
      // existing directory, surface a pinned top-of-list row that activates as
      // "add this directory as a workspace root". The check is independent of
      // which branch handles the rest of the suggestions below.
      const wantsFinalizer =
        parsed.commandSlash === 'open' || parsed.commandSlash === 'open-folder';
      const candidate = wantsFinalizer ? resolveCandidatePath(workingToken, baseDir) : null;
      let finalizer: Hit | null = null;
      if (candidate && typeof platform.isDirectory === 'function') {
        const isDir = await platform.isDirectory(candidate).catch(() => false);
        if (cancelled) return;
        if (isDir) {
          finalizer = {
            score: 100, // pin above per-level entries (which score 0)
            hit: { kind: 'openCurrentDir' as const, absPath: candidate, displayPath: candidate },
          };
        }
      }

      // Branch 2: a bare name fragment (no `/`, no `~/`) with workspace roots →
      // recursive name search across every root. Hits show the relative path so
      // duplicate basenames disambiguate (e.g. `spec.md` vs `docs/spec.md`).
      if (
        !workingToken.includes('/') &&
        !tildeExpanded &&
        !token.startsWith('~/') &&
        workspaceRoots.length > 0 &&
        workingToken.length >= 1 &&
        typeof platform.searchFiles === 'function'
      ) {
        const hits: Hit[] = [];
        if (finalizer) hits.push(finalizer);
        for (const root of workspaceRoots) {
          const rootPath = root.path;
          if (!rootPath || typeof platform.searchFiles !== 'function') continue;
          const results = await platform.searchFiles(rootPath, workingToken, { content: false });
          if (cancelled) return;
          for (const r of results) {
            const rel = relativeTo(rootPath, r.path);
            hits.push({
              score: 1,
              hit: {
                kind: 'pathEntry' as const,
                absPath: r.path,
                name: rel,
                isDir: r.isDir === true,
              },
            });
          }
        }
        if (!cancelled) setPathHits(hits.slice(0, 50));
        return;
      }

      // Branch 3: per-level browse (existing behavior), possibly rebased to home.
      if (!pathProviderRef.current || pathProviderRootRef.current !== baseDir) {
        pathProviderRef.current = createPathProvider({ platform, rootDir: baseDir });
        pathProviderRootRef.current = baseDir;
      }
      const { entries, baseDir: resolvedBase } = await pathProviderRef.current.list(workingToken);
      if (cancelled) return;
      const browseHits: Hit[] = entries.map((e) => ({
        score: 0,
        hit: {
          kind: 'pathEntry' as const,
          absPath: joinPath(resolvedBase, e.name),
          name: e.name,
          isDir: e.isDir,
        },
      }));
      setPathHits(finalizer ? [finalizer, ...browseHits] : browseHits);
    })();

    return () => {
      cancelled = true;
    };
  }, [activeParam, parsed.activeToken, parsed.commandSlash, workspaceRoots, platform]);

  const argHits: Hit[] = useMemo(() => {
    const param = activeParam;
    if (!param) return [];
    const token = parsed.activeToken;
    if (param.kind === 'enum') {
      const all: Hit[] = param.values.map((v) => ({
        score: 0,
        hit: { kind: 'enumValue' as const, value: v.value, label: v.label ?? v.value },
      }));
      if (!token) return all;
      const q = token.toLowerCase();
      return all.filter((h) => {
        const e = h.hit as Extract<HitKind, { kind: 'enumValue' }>;
        return (e.value + ' ' + e.label).toLowerCase().includes(q);
      });
    }
    if (param.kind === 'shell') {
      const q = parsed.query.trim();
      const hits: Hit[] = [];
      // A "Run: <typed>" row so the user can always run exactly what they typed.
      if (q) hits.push({ score: 1, hit: { kind: 'shellCommand' as const, command: q, literal: true } });
      for (const e of topCommands(historyEntries, q)) {
        if (q && e.command === q) continue; // avoid duplicating the literal row
        hits.push({ score: 0, hit: { kind: 'shellCommand' as const, command: e.command, count: e.count } });
      }
      return hits;
    }
    if (param.kind === 'number') {
      const hits: Hit[] = [];
      const parsedNum = parseZoomInput(token, param.range ?? [0, Number.POSITIVE_INFINITY]);
      if (parsedNum) {
        // Live preview hit: echo the user's chosen scale, plus a '→ Y' arrow
        // hint when clamping fired (e.g. typed '2.3' → '2.3× → 1.6×').
        hits.push({
          score: 1,
          hit: {
            kind: 'numberPreset',
            value: parsedNum.value,
            displayUnit: param.displayUnit,
            raw: parsedNum.raw,
            clamped: parsedNum.clamped,
            parsedScale: parsedNum.scale,
          },
        });
      }
      for (const p of param.presets ?? []) {
        hits.push({
          score: 0,
          hit: { kind: 'numberPreset', value: p, displayUnit: param.displayUnit },
        });
      }
      return hits;
    }
    if (param.kind === 'tab') {
      return docs
        .filter((d) => d.id !== activeId)
        .map((d) => ({ score: 0, hit: { kind: 'docName' as const, docId: d.id } }));
    }
    if (param.kind === 'root') {
      return workspaceRoots.map((r) => ({
        score: 0,
        hit: { kind: 'wsName' as const, path: r.path ?? '', name: r.name, isDir: true },
      }));
    }
    if (param.kind === 'heading') {
      const headings = extractHeadings(activeDoc?.text ?? '');
      const q = token.toLowerCase();
      const filtered = q ? headings.filter((h) => h.text.toLowerCase().includes(q)) : headings;
      return filtered.map((h) => ({
        score: 0,
        hit: { kind: 'heading' as const, line: h.line, depth: h.depth, text: h.text },
      }));
    }
    // 'path' handled asynchronously via pathHits
    return [];
  }, [activeParam, parsed.activeToken, parsed.query, docs, activeId, workspaceRoots, activeDoc, historyEntries]);

  // ── Flat row list (drives keyboard navigation + render order) ──
  const rows: Hit[] = useMemo(() => {
    switch (parsed.mode) {
      case 'empty':
        return emptyHits;
      case 'help':
        return helpHits;
      case 'outline':
        return outlineHits;
      case 'command': {
        if (!parsed.commandSlash) return commandHits;
        return activeParam?.kind === 'path' ? pathHits : argHits;
      }
      case 'mixed': {
        const ws = wsHits === 'loading' || wsHits === null ? [] : wsHits;
        // Sort: keep rows grouped by section so the renderer's "header on
        // section change" logic emits each section header exactly once, and
        // within each section put NAME-style hits before CONTENT-style hits
        // (user can scan filenames first; content matches follow as backup).
        // User-customizable mixed-mode priority. `paletteMixedOrder` carries
        // the seven sortable buckets; non-sortable sections (Commands, Recent,
        // Path, Outline, ...) hold their default ranks. The renderer emits a
        // section header at every section change, so each bucket surfaces
        // with its own labelled group. See `HitSection` jsdoc.
        const all = [...commandHits, ...syncHits, ...ws];
        const stableIdx = new Map<Hit, number>();
        all.forEach((h, i) => stableIdx.set(h, i));
        const sectionRank: Record<HitSection, number> = {
          Commands: 0,
          Recent: 1,
          // Sortable buckets land between rank 2 and 8 in whichever order
          // the user persisted (or the default sequence).
          'Active doc — name': 99,
          'Open tabs': 99,
          'Workspace folders': 99,
          'Workspace files': 99,
          'Active doc — content': 99,
          'Open tabs content': 99,
          'Workspace content': 99,
          Path: 9,
          Outline: 10,
          Enum: 11,
          Numeric: 12,
          Terminal: 13,
          Help: 14,
        };
        mixedOrder.forEach((sec, i) => {
          sectionRank[sec] = 2 + i;
        });
        return [...all]
          .sort((a, b) => {
            const sa = sectionRank[sectionOf(a.hit, activeId)] ?? 99;
            const sb = sectionRank[sectionOf(b.hit, activeId)] ?? 99;
            if (sa !== sb) return sa - sb;
            return (stableIdx.get(a) ?? 0) - (stableIdx.get(b) ?? 0);
          })
          .slice(0, mixedCap);
      }
    }
  }, [parsed, emptyHits, helpHits, outlineHits, commandHits, activeParam, pathHits, argHits, syncHits, wsHits, mixedCap, mixedOrder, activeId]);

  // Whether to show a "Searching workspace…" placeholder under the rows.
  const wsLoading = parsed.mode === 'mixed' && wsHits === 'loading';

  // Reset selection to the top whenever the MODE changes (e.g. user switches
  // from mixed to '/', or palette reopens). Do NOT reset on `rows.length`
  // alone — async workspace results arriving mid-typing would yank the user's
  // selection back to the top. Instead just clamp into the new bounds.
  useEffect(() => {
    setSelected(0);
  }, [parsed.mode]);
  useEffect(() => {
    setSelected((s) => (rows.length === 0 ? 0 : Math.min(s, rows.length - 1)));
  }, [rows.length]);

  // Disarm hover-focus whenever the rendered list changes (typing, async
  // workspace results, mode switch). A real pointermove re-arms it. Without
  // this, an item sliding under a stationary cursor would fire mouseenter
  // and yank focus to a row the user never deliberately hovered.
  useEffect(() => {
    hoverArmedRef.current = false;
  }, [rows]);

  // Scroll the highlighted row into view on keyboard navigation. `auto` behavior
  // already respects prefers-reduced-motion (no animated scroll).
  useEffect(() => {
    if (!open) return;
    const el = document.getElementById(`${optionPrefix}-${selected}`);
    el?.scrollIntoView({ block: 'nearest', behavior: 'auto' });
  }, [open, selected, optionPrefix]);

  // ── Activation ──
  const activate = useCallback(
    async (row: Hit, chainOpen: boolean): Promise<void> => {
      const close = (): void => {
        if (!chainOpen) setOpen(false);
      };
      const h = row.hit;
      const snap = useDocStore.getState();

      switch (h.kind) {
        case 'command': {
          const cmd = commands.find((c) => c.id === h.commandId);
          if (!cmd || !cmd.enabled) return;
          const args = buildArgsFromInput(cmd, parsed);
          const result = (await cmd.run(buildCtxFromState(platform), args)) as
            | CommandRunResult
            | undefined;
          if (!result?.keepOpen) close();
          return;
        }
        case 'tab':
        case 'docName': {
          snap.activateDoc(h.docId);
          close();
          return;
        }
        case 'docContent': {
          if (h.docId !== snap.activeId) snap.activateDoc(h.docId);
          snap.setPendingJumpLine(h.line);
          close();
          return;
        }
        case 'recent': {
          await openPath(platform, h.path);
          close();
          return;
        }
        case 'wsName': {
          // Directories add as a workspace root; files open in a tab.
          if (h.isDir) {
            const ref = { id: h.path, path: h.path, name: h.name };
            snap.addWorkspaceRoot(ref);
            void platform.pushRecentWorkspace(ref).catch(() => {});
          } else {
            await openPath(platform, h.path);
          }
          close();
          return;
        }
        case 'wsContent': {
          await openPath(platform, h.path);
          snap.setPendingJumpLine(h.line);
          close();
          return;
        }
        case 'pathEntry': {
          if (h.isDir) {
            // Descend: replace the active segment (trailing chars after the
            // last space *or* slash) with `<name>/`, preserving the command
            // prefix like `/open `.
            setRaw(raw.replace(/[^ /]*$/, h.name + '/'));
            return;
          }
          await openPath(platform, h.absPath);
          close();
          return;
        }
        case 'openCurrentDir': {
          // Finalize a typed folder path: add it as a workspace root and close.
          // Always uses the displayed basename so the sidebar label matches what
          // the user typed.
          const name = h.absPath.split(/[\\/]/).filter(Boolean).pop() ?? h.absPath;
          const ref = { id: h.absPath, path: h.absPath, name };
          snap.addWorkspaceRoot(ref);
          void platform.pushRecentWorkspace(ref).catch(() => {});
          close();
          return;
        }
        case 'heading': {
          snap.setPendingJumpLine(h.line);
          close();
          return;
        }
        case 'enumValue':
        case 'numberPreset': {
          // Commit the preset/value as the active arg, then run the command.
          if (parsed.mode !== 'command' || !parsed.commandSlash) return;
          const cmd = findCommandBySlash(commands, '/' + parsed.commandSlash);
          if (!cmd || !cmd.enabled) return;
          const args = buildArgsFromInput(cmd, parsed);
          const param = cmd.params?.[parsed.activeArgIndex];
          // enumValue carries a string; numberPreset carries a number — both are
          // committed as the active param's value.
          if (param) args[param.name] = h.value;
          const result = (await cmd.run(buildCtxFromState(platform), args)) as
            | CommandRunResult
            | undefined;
          if (!result?.keepOpen) close();
          return;
        }
        case 'shellCommand': {
          if (parsed.mode !== 'command' || !parsed.commandSlash) return;
          const cmd = findCommandBySlash(commands, '/' + parsed.commandSlash);
          if (!cmd || !cmd.enabled) return;
          const args = buildArgsFromInput(cmd, parsed);
          // Run the SELECTED command (a history row may differ from what's typed).
          const shellParam = cmd.params?.find((p) => p.kind === 'shell');
          if (shellParam) args[shellParam.name] = h.command;
          const result = (await cmd.run(buildCtxFromState(platform), args)) as
            | CommandRunResult
            | undefined;
          if (!result?.keepOpen) close();
          return;
        }
        case 'help': {
          setRaw(h.prefix);
          return;
        }
      }
    },
    [commands, parsed, platform, raw, setOpen],
  );

  // ── Keyboard ──
  const onKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLInputElement>): void => {
      const last = rows.length - 1;
      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          if (raw !== '') setRaw('');
          else setOpen(false);
          return;
        case 'ArrowDown':
          e.preventDefault();
          setSelected((i) => Math.min(last, i + 1));
          return;
        case 'ArrowUp':
          e.preventDefault();
          setSelected((i) => Math.max(0, i - 1));
          return;
        case 'Home':
          e.preventDefault();
          setSelected(0);
          return;
        case 'End':
          e.preventDefault();
          setSelected(last < 0 ? 0 : last);
          return;
        case 'PageDown':
          e.preventDefault();
          setSelected((i) => Math.min(last, i + PAGE));
          return;
        case 'PageUp':
          e.preventDefault();
          setSelected((i) => Math.max(0, i - PAGE));
          return;
        case 'Enter': {
          e.preventDefault();
          const row = rows[selected];
          if (row) void activate(row, e.metaKey || e.ctrlKey);
          return;
        }
        case 'Tab': {
          e.preventDefault();
          const row = rows[selected];
          if (!row) return;
          const h = row.hit;
          if (h.kind === 'command') {
            const cmd = commands.find((c) => c.id === h.commandId);
            if (cmd?.slash) setRaw(cmd.slash + (cmd.params?.length ? ' ' : ''));
            return;
          }
          if (h.kind === 'pathEntry') {
            // Replace only the active segment (after the last space or slash)
            // so the command prefix (e.g. `/open `) is preserved.
            setRaw(raw.replace(/[^ /]*$/, h.name + (h.isDir ? '/' : '')));
            return;
          }
          if (h.kind === 'enumValue') {
            setRaw(raw.replace(/[^ ]*$/, h.value));
            return;
          }
          if (h.kind === 'shellCommand') {
            setRaw('/' + (parsed.commandSlash ?? 'cmd') + ' ' + h.command);
            return;
          }
          return;
        }
        case '/': {
          // When a directory path-entry is highlighted, `/` descends into it.
          // Otherwise let the slash fall through to the input as a literal char.
          const row = rows[selected];
          if (row?.hit.kind === 'pathEntry' && row.hit.isDir) {
            e.preventDefault();
            // Replace the active segment (after the last space or slash) only,
            // keeping the command prefix intact.
            setRaw(raw.replace(/[^ /]*$/, row.hit.name + '/'));
          }
          return;
        }
        default:
          return;
      }
    },
    [raw, rows, selected, activate, commands, setOpen, parsed.commandSlash],
  );

  if (!open || typeof document === 'undefined') return null;

  const activeOptionId = rows[selected] ? `${optionPrefix}-${selected}` : undefined;

  // Pre-compute per-section counts for the headers.
  const sectionCounts = new Map<HitSection, number>();
  for (const row of rows) {
    const sec = sectionOf(row.hit, activeId);
    sectionCounts.set(sec, (sectionCounts.get(sec) ?? 0) + 1);
  }

  // Render rows grouped by section, with a presentational header per group.
  const children: ReactNode[] = [];
  let prevSection: HitSection | null = null;
  rows.forEach((row, i) => {
    const sec = sectionOf(row.hit, activeId);
    if (sec !== prevSection) {
      children.push(
        <li key={`section-${sec}-${i}`} role="presentation" className="mp-palette__section">
          <span className="mp-palette__section-name">{sec}</span>
          <span className="mp-palette__section-count">{sectionCounts.get(sec)}</span>
        </li>,
      );
      prevSection = sec;
    }
    children.push(
      <li
        id={`${optionPrefix}-${i}`}
        key={`row-${i}`}
        role="option"
        aria-selected={i === selected}
        className="mp-palette__row"
        data-selected={i === selected ? '' : undefined}
        data-section={sec}
        onMouseEnter={() => {
          // Only follow the cursor when the user actually moved it. See
          // hoverArmedRef above. setSelected from re-render fluttering is
          // exactly the bug this guards.
          if (hoverArmedRef.current) setSelected(i);
        }}
        onClick={() => void activate(row, false)}
      >
        {renderRow(row, commands, docs, parsed.query)}
      </li>,
    );
  });

  return createPortal(
    <div
      className="mp-palette"
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
      onClick={() => setOpen(false)}
    >
      <div className="mp-palette__panel" onClick={(e) => e.stopPropagation()}>
        <input
          ref={inputRef}
          role="combobox"
          aria-expanded="true"
          aria-controls={listboxId}
          aria-activedescendant={activeOptionId}
          aria-autocomplete="list"
          className="mp-palette__input"
          placeholder="Search, / for commands, # for headings, ? for help"
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          onKeyDown={onKeyDown}
          // Suppress macOS text replacement / browser autofill / mobile
          // capitalization — they steal focus and surface a distracting
          // suggestion chip when typing things like a name.
          spellCheck={false}
          autoCorrect="off"
          autoCapitalize="off"
          autoComplete="off"
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
        />
        <ul
          id={listboxId}
          role="listbox"
          aria-label="Results"
          className="mp-palette__results"
          // Real pointer movement re-arms hover-focus. The next mouseenter
          // on a row will follow the cursor; mouseenters fired purely by
          // the list re-rendering under a stationary cursor (typing, async
          // results) are ignored.
          onPointerMove={() => {
            hoverArmedRef.current = true;
          }}
        >
          {children}
        </ul>
        {wsLoading && (
          <div className="mp-palette__status" role="status" aria-live="polite">
            Searching workspace…
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

// ─────────────────────────── Row rendering ───────────────────────────

/**
 * Format a multiplier (e.g. 1.25) for display.
 *  - 'percent' scale → '125%' (rounded, no decimals).
 *  - 'multiplier' scale → '1.25×' (up to 2 decimal places, trimmed).
 */
function formatZoomDisplay(
  multiplier: number,
  scale: 'percent' | 'multiplier',
): string {
  if (scale === 'percent') {
    return Math.round(multiplier * 100) + '%';
  }
  const fixed = multiplier.toFixed(2).replace(/\.?0+$/, '');
  return fixed + '×';
}

/** Wrap query occurrences in `text` with <mark> for visual highlight. */
function highlight(text: string, query: string): ReactNode {
  const q = query.trim();
  if (!q) return text;
  const lower = text.toLowerCase();
  const needle = q.toLowerCase();
  if (!lower.includes(needle)) return text;
  const out: ReactNode[] = [];
  let from = 0;
  let idx = lower.indexOf(needle, from);
  let key = 0;
  while (idx >= 0) {
    if (idx > from) out.push(text.slice(from, idx));
    out.push(
      <mark className="mp-palette__hl" key={key++}>
        {text.slice(idx, idx + q.length)}
      </mark>,
    );
    from = idx + q.length;
    idx = lower.indexOf(needle, from);
  }
  if (from < text.length) out.push(text.slice(from));
  return out;
}

/** Extract the parent dir of a path (handles `/` and `\` separators). */
function dirname(path: string): string {
  const idx = Math.max(path.lastIndexOf('/'), path.lastIndexOf('\\'));
  return idx <= 0 ? '' : path.slice(0, idx);
}

/** Extract the basename of a path. */
function basename(path: string): string {
  const idx = Math.max(path.lastIndexOf('/'), path.lastIndexOf('\\'));
  return idx < 0 ? path : path.slice(idx + 1);
}

/**
 * Truncate `path` with an ellipsis IN THE MIDDLE, preserving both ends. So
 * `/Users/me/projects/foo/bar/baz.md` becomes `/Users/me/…/bar/baz.md`. The
 * file name (basename) and the workspace root prefix both stay visible, which
 * matters most when several files share a basename across roots.
 */
function middleEllipsis(path: string, maxLen = 48): string {
  if (path.length <= maxLen) return path;
  const ellipsis = '…';
  const remaining = maxLen - ellipsis.length;
  const tailLen = Math.ceil(remaining * 0.6);
  const headLen = remaining - tailLen;
  return path.slice(0, headLen) + ellipsis + path.slice(path.length - tailLen);
}

function renderRow(
  row: Hit,
  commands: Command[],
  docs: OpenDoc[],
  query: string,
): ReactNode {
  const h: HitKind = row.hit;
  switch (h.kind) {
    case 'command': {
      const c = commands.find((cmd) => cmd.id === h.commandId);
      if (!c) return null;
      const Icon = c.icon;
      return (
        <div className="mp-palette__row-inner" data-disabled={c.enabled ? undefined : ''}>
          {Icon && <Icon size={14} aria-hidden />}
          <span className="mp-palette__row-name">{highlight(c.name, query)}</span>
          {c.slash && <span className="mp-palette__row-slash">{c.slash}</span>}
          {c.shortcut && <span className="mp-palette__row-shortcut">{c.shortcut}</span>}
        </div>
      );
    }
    case 'tab':
    case 'docName': {
      const d = docs.find((doc) => doc.id === h.docId);
      const name = d?.ref?.name ?? 'Untitled';
      const path = d?.ref?.path;
      const parent = path ? dirname(path) : '';
      return (
        <>
          <span className="mp-palette__row-name">{highlight(name, query)}</span>
          {parent && (
            <span className="mp-palette__row-path" title={path}>
              {middleEllipsis(parent)}
            </span>
          )}
        </>
      );
    }
    case 'recent': {
      const parent = h.path ? dirname(h.path) : '';
      return (
        <>
          <span className="mp-palette__row-name">{highlight(h.name, query)}</span>
          {parent && (
            <span className="mp-palette__row-path" title={h.path}>
              {middleEllipsis(parent)}
            </span>
          )}
        </>
      );
    }
    case 'docContent': {
      const d = docs.find((doc) => doc.id === h.docId);
      const file = d?.ref?.name ?? 'Untitled';
      return (
        <>
          <span className="mp-palette__row-content">
            <span className="mp-palette__row-line">L{h.line}</span>
            <span className="mp-palette__row-snippet">{highlight(h.snippet, query)}</span>
          </span>
          <span className="mp-palette__row-path" title={d?.ref?.path ?? file}>
            {file}
          </span>
        </>
      );
    }
    case 'wsContent': {
      return (
        <>
          <span className="mp-palette__row-content">
            <span className="mp-palette__row-line">L{h.line}</span>
            <span className="mp-palette__row-snippet">{highlight(h.snippet, query)}</span>
          </span>
          <span className="mp-palette__row-path" title={h.path}>
            {h.name}
          </span>
        </>
      );
    }
    case 'wsName': {
      // h.name may already be a relative path (recursive-search results put
      // 'docs/spec.md' in name). Split for cleaner display.
      const displayName = basename(h.name) + (h.isDir ? '/' : '');
      const parent = dirname(h.name) || dirname(h.path);
      return (
        <>
          <span className="mp-palette__row-name">{highlight(displayName, query)}</span>
          {parent && (
            <span className="mp-palette__row-path" title={h.path}>
              {middleEllipsis(parent)}
            </span>
          )}
        </>
      );
    }
    case 'pathEntry': {
      const displayName = basename(h.name) + (h.isDir ? '/' : '');
      const parent = dirname(h.name) || dirname(h.absPath);
      return (
        <>
          <span className="mp-palette__row-name">{highlight(displayName, query)}</span>
          {parent && (
            <span className="mp-palette__row-path" title={h.absPath}>
              {middleEllipsis(parent)}
            </span>
          )}
        </>
      );
    }
    case 'openCurrentDir':
      return (
        <>
          <span className="mp-palette__row-name">Open this folder</span>
          <span className="mp-palette__row-path" title={h.absPath}>
            {middleEllipsis(h.displayPath)}
          </span>
        </>
      );
    case 'heading':
      return (
        <span className="mp-palette__row-heading" data-depth={h.depth}>
          <span className="mp-palette__row-hashes">{'#'.repeat(h.depth)}</span>{' '}
          {highlight(h.text, query)}
        </span>
      );
    case 'enumValue':
      return <span className="mp-palette__row-name">{h.label}</span>;
    case 'numberPreset': {
      // Live-preview hit (user is typing): echo their scale, plus a '→' arrow
      // when clamping fired. Static preset chips have no `parsedScale` and
      // render as percent.
      if (h.parsedScale && typeof h.raw === 'number') {
        const typed = formatZoomDisplay(h.raw, h.parsedScale);
        if (h.clamped) {
          const final = formatZoomDisplay(h.value, h.parsedScale);
          return (
            <span className="mp-palette__row-name">
              {typed}{' '}
              <span className="mp-palette__row-slash">→ {final}</span>
            </span>
          );
        }
        return <span className="mp-palette__row-name">{typed}</span>;
      }
      return (
        <span className="mp-palette__row-name">
          {formatZoomDisplay(h.value, h.displayUnit === 'x' ? 'multiplier' : 'percent')}
        </span>
      );
    }
    case 'shellCommand':
      return (
        <span className="mp-palette__row-inner">
          <TerminalIcon size={14} aria-hidden />
          <span className="mp-palette__row-name">
            {h.literal ? `Run: ${h.command}` : highlight(h.command, query)}
          </span>
          {typeof h.count === 'number' && (
            <span className="mp-palette__row-shortcut">×{h.count}</span>
          )}
        </span>
      );
    case 'help':
      return (
        <span className="mp-palette__row-help">
          <kbd className="mp-palette__row-prefix">{h.prefix}</kbd>
          <span className="mp-palette__row-desc">{h.description}</span>
        </span>
      );
  }
}
